import type { components } from './schema';
import { HttpClient, type ClientOptions } from './http';

export * from './http';

type S = components['schemas'];
export type Plan = S['Plan'];
export type PlanCreate = S['PlanCreate'];
export type PlanUpdate = S['PlanUpdate'];
export type PlanChanges = S['PlanChanges'];
export type PlanPage = S['PlanPage'];
export type Bootstrap = S['Bootstrap'];
export type Member = S['Member'];
export type MemberCreate = S['MemberCreate'];
export type MemberUpdate = S['MemberUpdate'];
export type MemberStatus = S['MemberStatus'];
export type SignupCreate = S['SignupCreate'];
/** Where an emailed sign-in link should land. */
export type LinkTarget = NonNullable<SignupCreate['client']>;
export type Household = S['Household'];
export type Device = S['Device'];
export type DeviceAdmin = S['DeviceWithStatus'];
export type Preferences = S['Preferences'];
export type PreferencesUpdate = S['PreferencesPatch'];
export type Weather = S['Weather'];
export type GeocodeResult = S['GeocodeResult'];
export type PairingCode = S['PairingCode'];
export type PairingStatus = S['PairingStatus'];
export type TokenResponse = S['TokenResponse'];
export type Task = S['Task'];
export type TaskCreate = S['TaskCreate'];
export type TaskUpdate = S['TaskUpdate'];
export type TaskPage = S['TaskPage'];
export type TaskChanges = S['TaskChanges'];
export type Habit = S['Habit'];
export type HabitCreate = S['HabitCreate'];
export type HabitUpdate = S['HabitUpdate'];
export type HabitLog = S['HabitLog'];
export type Alarm = S['Alarm'];
export type AlarmCreate = S['AlarmCreate'];
export type AlarmUpdate = S['AlarmUpdate'];
export type AlarmList = S['AlarmList'];
export type PrayerTimes = S['PrayerTimes'];
export type PrayerMethod = S['PrayerMethod'];
export type QuranTranslation = S['QuranTranslation'];
export type QuranSurah = S['QuranSurah'];
export type QuranSurahPage = S['QuranSurahPage'];
export type QuranAyah = S['QuranAyah'];
export type QuranVerse = S['QuranVerse'];
export type ExchangeRates = S['ExchangeRates'];
export type ExchangeCurrency = S['ExchangeCurrency'];
export type CurrencyCode = S['CurrencyCode'];
export type Kanji = S['Kanji'];
export type KanjiLevel = S['KanjiLevel'];
export type KanjiProgress = S['KanjiProgress'];
export type KanjiProgressPut = S['KanjiProgressPut'];
export type Theme = Preferences['theme'];
export type Scene = Preferences['scene'];
export type FamilyBackground = S['FamilyBackground'];

/** Typed endpoint wrappers over the generated OpenAPI schema. */
export function createApi(opts: ClientOptions) {
  const http = new HttpClient(opts);
  const r = http.request.bind(http);
  return {
    http,
    pairing: {
      create: (device_name?: string) => r<PairingCode>('/v1/pairing/codes', { method: 'POST', auth: false, body: { device_name } }),
      status: (code: string, poll_token: string) =>
        r<PairingStatus>(`/v1/pairing/codes/${encodeURIComponent(code)}`, { auth: false, query: { poll_token }, retries: 0 }),
      claim: (code: string, device_name?: string) => r<Device>('/v1/pairing/claims', { method: 'POST', body: { code, device_name } }),
    },
    auth: {
      /** App links need a PKCE `code_challenge`; the app keeps the verifier for `verify`. */
      requestLink: (email: string, client: LinkTarget = 'web', code_challenge?: string) =>
        r<void>('/v1/auth/magic-link', { method: 'POST', auth: false, body: { email, client, code_challenge } }),
      /** Nothing is created until the emailed link is opened (then `verify` signs the new admin in). */
      signup: (body: Omit<SignupCreate, 'client' | 'code_challenge'> & { client?: LinkTarget; code_challenge?: string }) =>
        r<void>('/v1/auth/signup', { method: 'POST', auth: false, body: { client: 'web', ...body } }),
      verify: async (token: string, code_verifier?: string) => {
        const t = await r<TokenResponse>('/v1/auth/magic-link/verify', { method: 'POST', auth: false, body: { token, code_verifier } });
        await http.acceptTokens(t);
        return t;
      },
      logout: async () => {
        const refresh_token = await http.storedRefreshToken();
        try {
          await r<void>('/v1/auth/logout', { method: 'POST', auth: false, body: refresh_token ? { refresh_token } : undefined, retries: 1 });
        } finally {
          await http.forgetSession();
        }
      },
    },
    bootstrap: () => r<Bootstrap>('/v1/bootstrap'),
    plans: {
      list: (q: { from: string; to: string; member_id?: string; cursor?: string; limit?: number }) => r<PlanPage>('/v1/plans', { query: q }),
      changes: (since: string) => r<PlanChanges>('/v1/plans/changes', { query: { since } }),
      create: (body: PlanCreate, idempotencyKey: string) => r<Plan>('/v1/plans', { method: 'POST', body, idempotencyKey }),
      update: (id: string, body: PlanUpdate, idempotencyKey: string) =>
        r<Plan>(`/v1/plans/${id}`, { method: 'PATCH', body, idempotencyKey }),
      remove: (id: string, idempotencyKey: string) => r<void>(`/v1/plans/${id}`, { method: 'DELETE', idempotencyKey }),
    },
    tasks: {
      list: (q: { done_since?: string; member_id?: string; cursor?: string; limit?: number }) => r<TaskPage>('/v1/tasks', { query: q }),
      changes: (since: string) => r<TaskChanges>('/v1/tasks/changes', { query: { since } }),
      create: (body: TaskCreate, idempotencyKey: string) => r<Task>('/v1/tasks', { method: 'POST', body, idempotencyKey }),
      update: (id: string, body: TaskUpdate, idempotencyKey: string) =>
        r<Task>(`/v1/tasks/${id}`, { method: 'PATCH', body, idempotencyKey }),
      remove: (id: string, idempotencyKey: string) => r<void>(`/v1/tasks/${id}`, { method: 'DELETE', idempotencyKey }),
      rollover: (to: string | undefined, idempotencyKey: string) =>
        r<{ moved: number }>('/v1/tasks/rollover', { method: 'POST', body: to ? { to } : {}, idempotencyKey }),
    },
    habits: {
      list: () => r<{ items: Habit[] }>('/v1/habits'),
      create: (body: HabitCreate) => r<Habit>('/v1/habits', { method: 'POST', body }),
      update: (id: string, body: HabitUpdate) => r<Habit>(`/v1/habits/${id}`, { method: 'PATCH', body }),
      remove: (id: string) => r<void>(`/v1/habits/${id}`, { method: 'DELETE' }),
      logs: (from: string, to: string) => r<{ items: HabitLog[] }>('/v1/habits/logs', { query: { from, to } }),
      // PUT is naturally idempotent, so retries need no key.
      setLog: (id: string, date: string, done: boolean) =>
        r<HabitLog>(`/v1/habits/${id}/logs/${date}`, { method: 'PUT', body: { done } }),
    },
    // Display sessions only; each display sees just its own alarms.
    alarms: {
      list: () => r<AlarmList>('/v1/alarms'),
      create: (body: AlarmCreate, idempotencyKey: string) => r<Alarm>('/v1/alarms', { method: 'POST', body, idempotencyKey }),
      update: (id: string, body: AlarmUpdate, idempotencyKey: string) =>
        r<Alarm>(`/v1/alarms/${id}`, { method: 'PATCH', body, idempotencyKey }),
      remove: (id: string, idempotencyKey: string) => r<void>(`/v1/alarms/${id}`, { method: 'DELETE', idempotencyKey }),
      dismiss: (id: string, idempotencyKey: string) =>
        r<Alarm>(`/v1/alarms/${id}/dismiss`, { method: 'POST', idempotencyKey }),
    },
    preferences: {
      get: () => r<Preferences>('/v1/devices/me/preferences'),
      update: (body: PreferencesUpdate) => r<Preferences>('/v1/devices/me/preferences', { method: 'PATCH', body }),
      reset: () => r<Preferences>('/v1/devices/me/reset', { method: 'POST' }),
    },
    household: {
      update: (body: { name?: string; location?: GeocodeResult; prayer_method?: number; prayer_school?: 'standard' | 'hanafi' }) =>
        r<Household>('/v1/household', { method: 'PATCH', body }),
    },
    prayer: {
      times: () => r<PrayerTimes>('/v1/prayer-times', { retries: 1 }),
      methods: () => r<{ items: PrayerMethod[] }>('/v1/prayer-times/methods'),
    },
    quran: {
      surahs: () => r<{ items: QuranSurah[] }>('/v1/quran/surahs'),
      surah: (number: number, q: { translation?: QuranTranslation; from?: number; limit?: number } = {}) =>
        r<QuranSurahPage>(`/v1/quran/surahs/${number}`, { query: q }),
      verseOfDay: (translation?: QuranTranslation) => r<QuranVerse>('/v1/quran/verse-of-the-day', { query: { translation } }),
    },
    exchange: {
      rates: () => r<ExchangeRates>('/v1/exchange/rates', { retries: 1 }),
    },
    kanji: {
      list: (level?: KanjiLevel) => r<{ items: Kanji[] }>('/v1/kanji', { query: { level } }),
      // Display sessions only; each display keeps its own progress.
      progress: () => r<{ items: KanjiProgress[] }>('/v1/kanji/progress'),
      // PUT is naturally idempotent, so retries need no key.
      setProgress: (id: number, body: KanjiProgressPut) =>
        r<KanjiProgress>(`/v1/kanji/${id}/progress`, { method: 'PUT', body }),
    },
    members: {
      list: () => r<{ items: Member[] }>('/v1/members'),
      create: (body: MemberCreate) => r<Member>('/v1/members', { method: 'POST', body }),
      update: (id: string, body: MemberUpdate) => r<Member>(`/v1/members/${id}`, { method: 'PATCH', body }),
      remove: (id: string) => r<void>(`/v1/members/${id}`, { method: 'DELETE' }),
      /** Emails a fresh 72 h invite link. */
      invite: (id: string) => r<void>(`/v1/members/${id}/invite`, { method: 'POST' }),
    },
    devices: {
      list: () => r<{ items: DeviceAdmin[] }>('/v1/devices'),
      revoke: (id: string) => r<void>(`/v1/devices/${id}`, { method: 'DELETE' }),
      /** Display sessions only: leave this home (e.g. to pair with another); it ends the session. */
      unpairSelf: async () => {
        await r<void>('/v1/devices/me', { method: 'DELETE', retries: 1 });
        await http.forgetSession();
      },
    },
    weather: () => r<Weather>('/v1/weather', { retries: 1 }),
    geocode: (q: string) => r<{ items: GeocodeResult[] }>('/v1/geocode', { query: { q } }),
    clientLog: (body: { level: string; message: string; stack?: string; url?: string; app_version?: string; user_agent?: string }) =>
      r<void>('/v1/client-logs', { method: 'POST', body, auth: http.hasAccessToken(), retries: 0 }),
  };
}

export type Api = ReturnType<typeof createApi>;
